import type { ReactNode } from "react";
import {
  Alert,
  Button,
  buttonClasses,
  fmtLongDate,
  type PortalSignableDocument,
} from "@dearbill/shared";

interface SignedDocumentPanelProps {
  doc: PortalSignableDocument;
  onDone: () => void;
}

/**
 * A signed document's confirmation: the signing date, the signed PDF (open in
 * a new tab, or download) and the way back to the dashboard. The links are
 * short-lived presigned URLs; the dashboard's documents card carries fresh
 * ones on each load.
 */
export function SignedDocumentPanel({ doc, onDone }: SignedDocumentPanelProps): ReactNode {
  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-h3 text-midnight font-serif">{doc.title}</h1>
      <Alert tone="success" title="Signed. Thank you.">
        {doc.signedAt
          ? `You signed this document on ${fmtLongDate(doc.signedAt)}.`
          : "Your signature is recorded."}
      </Alert>
      {(doc.signedDocUrl || doc.signedDocDownloadUrl) && (
        <div className="flex flex-wrap gap-4">
          {doc.signedDocUrl && (
            <a
              href={doc.signedDocUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonClasses("outlined", "md")}
            >
              Open your signed copy
            </a>
          )}
          {doc.signedDocDownloadUrl && (
            <a href={doc.signedDocDownloadUrl} className={buttonClasses("soft", "md")}>
              Download your signed copy
            </a>
          )}
        </div>
      )}
      <div>
        <Button variant="secondary" size="md" onClick={onDone}>
          Back to your dashboard
        </Button>
      </div>
    </div>
  );
}
